import { makeAutoObservable } from "mobx";
import { Node } from "reactflow";
import bots from "./bots";
import {
  Journey,
  JourneyCategory,
  JourneyNodes,
  JourneyEvent,
  JouneryCard,
  Card,
  JourneyLog,
  FlowMap,
} from "../models";
import {
  getJourneys,
  getJourneyNodes,
  deleteJourney as deleteJourneyChathub,
  createJourney,
  updateJourney,
  duplicateJourney as duplicateJourneyChathub,
  getJourneyCategories,
  updateJourneyCategory,
  createJourneyCategory,
  deleteJourneyCategory,
  getJourneyEvents,
  getJourneyLogs,
  getCards,
  getForms,
  getAllJourneys,
  getTriggerableJourneys,
  getJourney,
  getJourneyFlowMap,
} from "../services/chathub";
import auth from "./auth";
import { DateTime } from "luxon";
import workspaces from "./workspaces";

type EventPaginate = {
  data: JourneyEvent[];
  total: number;
  last_page: number;
  current_page: number;
};

type LogPaginate = {
  data: JourneyLog[];
  total: number;
  last_page: number;
  current_page: number;
};

class JourneyStore {
  categories: JourneyCategory[] = [];
  journeys: Journey[] = [];
  journeyNodes: JourneyNodes | null = null;
  selectedCategory?: JourneyCategory;
  selectedJourney?: Journey;
  currentNode: Node | null = null;
  journeyEventsPaginate?: EventPaginate;
  journeyLogsPaginate?: LogPaginate;
  allJourneys?: JouneryCard[];
  triggerableJourneys?: JouneryCard[];
  flowMap?: FlowMap;
  cards?: Card[];
  forms?: JouneryCard[];
  filters: {
    archived: boolean;
  } = {
    archived: false,
  };

  constructor() {
    makeAutoObservable(this);
  }

  loadCategories = async () => {
    if (!bots.selectedBot) {
      return;
    }

    const categories = await getJourneyCategories(
      bots.selectedBot.id,
      bots.selectedBot.key,
      bots.selectedBot.password
    );

    this.setCategories(categories.map(this.categoryAdapter));
  };

  updateOrCreateCategory = async (categoryObj: JourneyCategory) => {
    if (!bots.selectedBot || !auth.user) {
      return;
    }

    if (categoryObj?.id) {
      const updatedCategory = await updateJourneyCategory(
        bots.selectedBot.id,
        bots.selectedBot.key,
        bots.selectedBot.password,
        categoryObj.id,
        categoryObj.name
      );
      const category = this.categoryAdapter(updatedCategory);
      const index = this.categories.findIndex((f) => f.id === category.id);
      if (index > -1) {
        this.categories[index] = category;
      }

      if (this.selectedCategory?.id === category.id) {
        this.selectCategory(category);
      }
    } else {
      const newCategory = await createJourneyCategory(
        bots.selectedBot.id,
        bots.selectedBot.key,
        bots.selectedBot.password,
        categoryObj.name
      );

      this.categories.unshift(this.categoryAdapter(newCategory));
    }
  };

  deleteCategory = async (category: JourneyCategory) => {
    if (!bots.selectedBot) {
      return;
    }

    if (category?.id) {
      await deleteJourneyCategory(
        bots.selectedBot.id,
        bots.selectedBot.key,
        bots.selectedBot.password,
        category.id
      );

      const index = this.categories.findIndex((f) => f.id === category.id);

      if (index > -1) {
        this.categories.splice(index, 1);
      }
    }
  };

  loadJourneys = async (
    filters?: any,
    page: number = 1,
    perPage: number = 10,
    search?: string,
    sortField?: string,
    sortOrder?: 'asc' | 'desc'
  ) => {
    if (!bots.selectedBot || !this.selectedCategory) {
      return;
    }

    const response = await getJourneys(
      bots.selectedBot.id,
      bots.selectedBot.key,
      bots.selectedBot.password,
      this.selectedCategory.id,
      filters,
      page,
      perPage,
      search,
      sortField,
      sortOrder
    );

    // Store total count for pagination
    const totalCount = response.total || 0;
    const journeys = response.data || [];
    
    this.setJourneys(journeys.map(this.journeyAdapter));
    return { data: this.journeys, total: totalCount };
  };

  loadJourney = async (id: number) => {
    if (!bots.selectedBot) {
      return;
    }

    const data = await getJourney(
      bots.selectedBot.id,
      bots.selectedBot.key,
      bots.selectedBot.password,
      id
    );
    const journey = this.journeyAdapter(data);
    this.setSelectedJourney(journey);
    return journey;
  };

  updateOrCreateJourney = async (journey: Journey, basicUpdates = false) => {
    if (!bots.selectedBot || !auth.user || !workspaces.selectedWorkspace) {
      return;
    }

    if (journey?.id) {
      const updatedJourney = await updateJourney(
        bots.selectedBot.id,
        bots.selectedBot.key,
        bots.selectedBot.password,
        journey.id,
        journey.name,
        journey.categoryId,
        journey.userId,
        journey.status,
        journey.isActive,
        journey.archived,
        auth.user.id,
        basicUpdates
          ? undefined
          : {
              ...journey.draftData,
              workspaceId: workspaces?.selectedWorkspace?.id,
              functionId: workspaces?.selectedFunction?.slug,
            }
      );
      const index = this.journeys.findIndex((f) => f.id === journey.id);
      if (index > -1) {
        const ref = [...this.journeys];
        if (
          (this.filters.archived && !journey.archived) ||
          (!this.filters.archived && journey.archived)
        ) {
          this.journeys.splice(index, 1);
        } else {
          ref[index] = this.journeyAdapter(updatedJourney);
          this.setJourneys(ref);
        }
      }
    } else {
      const newJourney = await createJourney(
        bots.selectedBot.id,
        bots.selectedBot.key,
        bots.selectedBot.password,
        journey.name,
        journey.categoryId,
        auth.user.id,
        journey.status,
        journey.isActive,
        journey.draftData
          ? {
              ...journey.draftData,
              workspaceId: workspaces?.selectedWorkspace?.id,
              functionId: workspaces?.selectedFunction?.slug,
            }
          : undefined
      );

      this.journeys.unshift(this.journeyAdapter(newJourney));
    }
  };

  deleteJourney = async (journey: Journey) => {
    if (!bots.selectedBot) {
      return;
    }

    if (journey?.id) {
      await deleteJourneyChathub(
        bots.selectedBot.id,
        bots.selectedBot.key,
        bots.selectedBot.password,
        journey.id
      );

      const index = this.journeys.findIndex((f) => f.id === journey.id);

      if (index > -1) {
        this.journeys.splice(index, 1);
      }
    }
  };

  duplicateJourney = async (journey: Journey) => {
    if (!bots.selectedBot || !auth.user) {
      return;
    }

    if (journey?.id) {
      const newJourney = await duplicateJourneyChathub(
        bots.selectedBot.id,
        bots.selectedBot.key,
        bots.selectedBot.password,
        journey.id,
        auth.user.id
      );

      this.journeys.unshift(this.journeyAdapter(newJourney));
    }
  };

  loadJourneyNodes = async () => {
    if (!bots.selectedBot) {
      return;
    }

    const journeyNodes = await getJourneyNodes(
      bots.selectedBot.id,
      bots.selectedBot.key,
      bots.selectedBot.password
    );

    this.setJourneyNodes(journeyNodes);
  };

  openNodeSettings = (node: Node) => {
    this.currentNode = node;
  };

  closeNodeSettings = () => {
    this.currentNode = null;
  };

  setCategories = (categories: JourneyCategory[]) => {
    this.categories = categories;
  };

  selectCategory = (category: JourneyCategory) => {
    this.selectedCategory = category;
  };

  setJourneys = (journeys: Journey[]) => {
    this.journeys = journeys;
  };

  setJourneyNodes = (journeyNodes: JourneyNodes) => {
    this.journeyNodes = journeyNodes;
  };

  categoryAdapter = (category: any): JourneyCategory => {
    return {
      id: category.id,
      name: category.name,
    };
  };

  loadJourneyEvents = async (journeyId: number, page = 1) => {
    if (!bots.selectedBot) {
      return;
    }

    const events = await getJourneyEvents(
      bots.selectedBot.id,
      bots.selectedBot.key,
      bots.selectedBot.password,
      journeyId,
      page
    );

    this.setJourneyEvents({
      ...events,
      data: events.data.map(this.journeyEventAdapter),
    });
  };

  loadJourneyLogs = async (journeyId: number, page = 1) => {
    if (!bots.selectedBot) {
      return;
    }

    const events = await getJourneyLogs(
      bots.selectedBot.id,
      bots.selectedBot.key,
      bots.selectedBot.password,
      journeyId,
      page
    );

    this.setJourneyLogs({
      ...events,
      data: events.data.map(this.journeyLogAdapter),
    });
  };

  loadAllJourneys = async () => {
    if (!bots.selectedBot) {
      return;
    }

    const journeys = await getAllJourneys(
      bots.selectedBot.id,
      bots.selectedBot.key,
      bots.selectedBot.password
    );

    this.setSetAllJourneys(journeys);
  };

  loadTriggerableJourneys = async () => {
    if (!bots.selectedBot) {
      return;
    }

    const journeys = await getTriggerableJourneys(
      bots.selectedBot.id,
      bots.selectedBot.key,
      bots.selectedBot.password
    );

    this.setTriggerableJourneys(journeys);
  };

  loadFlowMap = async () => {
    if (!bots.selectedBot) {
      return;
    }

    const flowMap = await getJourneyFlowMap(
      bots.selectedBot.id,
      bots.selectedBot.key,
      bots.selectedBot.password
    );

    this.setFlowMap(flowMap);
    return flowMap as FlowMap;
  };

  loadCards = async () => {
    if (!bots.selectedBot) {
      return;
    }

    const { data } = await getCards(
      bots.selectedBot.id,
      bots.selectedBot.key,
      bots.selectedBot.password,
      1,
      100000000,
      undefined,
      true,
      true
    );

    this.setSetCards(data);
  };

  loadForms = async () => {
    if (!bots.selectedBot) {
      return;
    }

    const { data } = await getForms(
      bots.selectedBot.id,
      bots.selectedBot.key,
      bots.selectedBot.password,
      1,
      100000000
    );

    this.setSetForms(data);
  };

  private setJourneyEvents(paginate: EventPaginate) {
    this.journeyEventsPaginate = paginate;
  }

  private setJourneyLogs(paginate: LogPaginate) {
    this.journeyLogsPaginate = paginate;
  }

  private setSetCards(cards?: Card[]) {
    this.cards = cards;
  }

  private setSetAllJourneys(journeys?: JouneryCard[]) {
    this.allJourneys = journeys;
  }

  private setTriggerableJourneys(journeys?: JouneryCard[]) {
    this.triggerableJourneys = journeys;
  }

  private setFlowMap(flowMap?: FlowMap) {
    this.flowMap = flowMap;
  }

  private setSetForms(forms?: JouneryCard[]) {
    this.forms = forms;
  }

  journeyAdapter = (journey: any): Journey => {
    return {
      id: journey.id,
      name: journey.name,
      categoryId: journey.journey_category_id,
      userId: journey.user_id,
      status: journey.status,
      draftData: journey?.draft_data,
      publishedData: journey?.published_data,
      eventsCount: journey.events_count,
      completionRate: parseInt(journey.completion_rate ?? "0"),
      analytics: journey.analytics,
      isActive: journey.is_active,
      archived: journey.archived,
      createdAt: DateTime.fromISO(journey.created_at, {
        zone: "utc",
      }).toLocal(),
      updatedAt: DateTime.fromISO(journey.updated_at, {
        zone: "utc",
      }).toLocal(),
    };
  };

  journeyEventAdapter = (item: any): JourneyEvent => {
    return {
      id: item.id,
      journeyData: item?.journey_data,
      meta: item?.meta,
      status: item.status,
      failedReason: item.failed_reason,
      createdAt: DateTime.fromISO(item.created_at, {
        zone: "utc",
      }).toLocal(),
      updatedAt: DateTime.fromISO(item.updated_at, {
        zone: "utc",
      }).toLocal(),
      logs: item.logs?.map((l: any) => ({
        id: l.id,
        node: l.node,
        level: l.level,
        content: l.content,
        meta: l.meta,
        createdAt: l.created_at,
        updatedAt: l.updated_at,
      })),
    };
  };

  journeyLogAdapter = (item: any): JourneyLog => {
    return {
      id: item.id,
      content: item?.content,
      meta: item?.meta,
      createdAt: DateTime.fromISO(item.created_at, {
        zone: "utc",
      }).toLocal(),
      updatedAt: DateTime.fromISO(item.updated_at, {
        zone: "utc",
      }).toLocal(),
    };
  };

  private setSelectedJourney(journey: Journey) {
    this.selectedJourney = journey;
  }

  setFilters = (filters: { archived: boolean }) => {
    this.filters = filters;
  };
}

export default new JourneyStore();
